<template>
  <transition name="cp-fade">
    <div class="cp-backdrop" v-if="open" @mousedown.self="close">
      <div class="cp-panel" role="dialog" aria-label="Buscador">

        <div class="cp-mode-banner" v-if="pinTarget === 'space'">
          <AppIcon icon="push-pin" fill></AppIcon>
          Tocá una vista o acción para sumarla a Mi espacio
        </div>

        <div class="cp-input-row">
          <AppIcon icon="search" class="cp-input-icon"></AppIcon>
          <input ref="input"
                 type="text"
                 class="cp-input"
                 v-model="query"
                 placeholder="Buscá vistas, clientes, proveedores, proyectos o acciones..."
                 autocomplete="off"
                 spellcheck="false"
                 @keydown.down.prevent="moveSelection(1)"
                 @keydown.up.prevent="moveSelection(-1)"
                 @keydown.enter.prevent="confirmSelection"
                 @keydown.esc.prevent="close">
          <span class="cp-esc-hint" @click="close">esc</span>
        </div>

        <div class="cp-results" ref="results">
          <template v-for="(section, sIndex) in sections">
            <div class="cp-section-label" :key="'label-' + sIndex">{{ section.label }}</div>
            <div v-for="item in section.items"
                 :key="itemKey(item)"
                 class="cp-item"
                 :class="[itemTypeClass(item), { 'cp-item--active': flatIndex(item) === selectedIndex }]"
                 :data-index="flatIndex(item)"
                 @mouseenter="selectedIndex = flatIndex(item)"
                 @click="onItemClick(item)">
              <span class="cp-item-icon"><AppIcon :name="item.icon" /></span>
              <span class="cp-item-title">{{ item.title }}</span>
              <span class="cp-item-subtitle" v-if="item.subtitle">{{ item.subtitle }}</span>
              <span class="cp-item-notes-match" v-if="item.matchesNotes">
                <AppIcon name="sticky_note_2" /> En notas
              </span>
              <span class="cp-item-group" v-if="item.group">{{ item.group }}</span>
            </div>
          </template>

          <div class="cp-loading" v-if="dataLoading">
            <b-spinner small class="mr-2"></b-spinner>Buscando en el sistema...
          </div>

          <div class="cp-empty" v-if="!sections.length && !dataLoading">
            <template v-if="query.trim()">
              <AppIcon icon="binoculars" font-scale="1.4" class="mb-2"></AppIcon>
              <div>No se encontró nada para "{{ query }}"</div>
            </template>
            <template v-else>
              <AppIcon icon="lightning" font-scale="1.4" class="mb-2"></AppIcon>
              <div>Empezá a escribir para buscar en todo el sistema</div>
            </template>
          </div>
        </div>

        <div class="cp-footer">
          <span><kbd>↑</kbd><kbd>↓</kbd> navegar</span>
          <span><kbd>Enter</kbd> abrir</span>
          <span><kbd>Esc</kbd> cerrar</span>
          <span class="cp-footer-commands">
            <span class="cp-footer-command"><kbd>#</kbd> tableros</span>
            <span class="cp-footer-command"><kbd>@</kbd> acciones</span>
            <span class="cp-footer-command"><kbd>/</kbd> accesos directos</span>
          </span>
        </div>

      </div>
    </div>
  </transition>
</template>

<script>
import Session from '@/handler/session'
import Storage from '@/handler/storageLocal'
import Search from './search'
import mySpaceShortcuts from '@/handler/mySpaceShortcuts'
import { EventBus } from '@/handler/event-bus'
import Profiles from '@/config/profiles'

const RECENTS_KEY_PREFIX = 'recent_views_user_'
const RECENTS_MAX = 8
const DATA_SEARCH_MIN_LENGTH = 3
const DATA_SEARCH_DEBOUNCE_MS = 300

export default {
  name: 'CommandPalette',
  data: () => {
    return {
      enabled: false,
      open: false,
      // Fijar en Favoritos ya no pasa por acá — vive en la estrella de cada
      // tab (DefaultTabsLayout.vue), sobre la vista ya renderizada. 'space'
      // es el único modo que esta paleta entiende: se activa solo cuando Mi
      // Espacio la abre para sumar sus propios accesos (ver mySpace.vue).
      pinTarget: 'shortcuts',
      query: '',
      index: [],
      dashboardIndex: [],
      actionIndex: [],
      shortcutIndex: [],
      recentIndex: [],
      selectedIndex: 0,
      dataResults: [],
      dataLoading: false,
      dataDebounceTimer: null,
      dataRequestIdCounter: 0,
    }
  },
  created() {
    EventBus.$on('Auth:auth', this.setup)
    EventBus.$on('CommandPalette:open', this.show)
  },
  mounted() {
    this.setup()
    document.addEventListener('keydown', this.onGlobalKeydown)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onGlobalKeydown)
    EventBus.$off('Auth:auth', this.setup)
    EventBus.$off('CommandPalette:open', this.show)
  },
  watch: {
    query() {
      this.selectedIndex = 0
      this.scheduleDataSearch()
    },
    // Cada navegación se registra en "recientes" (con título legible de la ruta).
    $route(to) {
      this.trackRecent(to)
      if (this.open) this.close()
    },
  },
  computed: {
    // "#<módulo>" — la única forma de que el dashboard de un módulo
    // aparezca como resultado (las búsquedas normales lo excluyen a
    // propósito, ver search.js). null si la query actual no es ese comando.
    dashboardCommandQuery() {
      return Search.matchDashboardCommand(this.query)
    },
    // "@<acción>" — la única forma de buscar acciones puntuales de
    // actions.js. Fuera de este comando, las acciones no son parte de los
    // resultados normales (ver search.js/buildIndex, que ya no las incluye).
    actionCommandQuery() {
      return Search.matchActionCommand(this.query)
    },
    // "/<término>" — única forma de buscar accesos directos (mismo criterio
    // que "#"/"@": fuera de este comando no compiten con vistas/interfaces
    // en la búsqueda con texto). Siguen apareciendo igual en el estado
    // vacío del buscador (ver defaultSections más abajo) — ahí no hay
    // resultados de vistas/interfaces con los que compitan.
    shortcutCommandQuery() {
      return Search.matchShortcutCommand(this.query)
    },
    sections() {
      if (this.dashboardCommandQuery !== null) {
        var dashboardTerm = this.dashboardCommandQuery.trim()
        var dashboards = dashboardTerm
          ? Search.search(dashboardTerm, this.dashboardIndex)
          : this.dashboardIndex
        return dashboards.length ? [{ label: 'Tableros', items: dashboards.slice(0, 8) }] : []
      }

      if (this.actionCommandQuery !== null) {
        var actionTerm = this.actionCommandQuery.trim()
        var actionResults = actionTerm
          ? Search.search(actionTerm, this.actionIndex)
          : this.actionIndex
        return actionResults.length ? [{ label: 'Acciones', items: actionResults.slice(0, 8) }] : []
      }

      if (this.shortcutCommandQuery !== null) {
        var shortcutTerm = this.shortcutCommandQuery.trim()
        var shortcutResults = shortcutTerm
          ? Search.search(shortcutTerm, this.shortcutIndex)
          : this.shortcutIndex
        return shortcutResults.length ? [{ label: 'Accesos directos', items: shortcutResults.slice(0, 8) }] : []
      }

      if (this.query.trim()) {
        var results = Search.search(this.query, this.index)

        var byType = { view: [], interface: [] }
        results.forEach(item => {
          if (byType[item.type]) byType[item.type].push(item)
        })

        var recentResults = Search.search(this.query, this.recentIndex).slice(0, 5)

        // Orden fijo: Recientes -> Vistas -> Interfaces -> Clientes ->
        // Proveedores y Proyectos. Accesos directos, acciones y dashboards de módulo
        // quedan afuera del flujo normal — cada uno tiene su propio comando
        // ("/", "@", "#").
        //
        // Un resultado que ya aparece en Recientes no se repite más abajo:
        // "Recientes" y "Vistas"/"Interfaces"/"Clientes"/"Proveedores" son
        // dos índices separados (recentIndex vs. index/dataResults), así que
        // la misma vista puede matchear en los dos y listarse dos veces con
        // dos formas distintas del mismo destino (url resuelta vs. ruta con
        // nombre) -- itemPath() ya normaliza ambas a la misma ruta final
        // (se usa para "sumar a Mi espacio"), así que sirve también acá para
        // reconocer que son la misma vista y sacarla de las secciones de abajo.
        // Tampoco tiene sentido ofrecer como resultado la vista donde ya
        // estás parado -- getRecents() ya la excluye de Recientes por su
        // cuenta (ver su propio filter), pero Vistas/Interfaces/Clientes/
        // Proveedores vienen de índices que no conocen la ruta actual.
        var currentPath = this.$route.fullPath
        var recentPaths = {}
        recentResults.forEach(item => {
          var path = this.itemPath(item)
          if (path) recentPaths[path] = true
        })
        var notAlreadyShown = item => {
          var path = this.itemPath(item)
          if (!path) return true
          if (path === currentPath) return false
          return !recentPaths[path]
        }

        var sections = []
        if (recentResults.length) sections.push({ label: 'Recientes', items: recentResults })

        var viewResults = byType.view.filter(notAlreadyShown)
        var interfaceResults = byType.interface.filter(notAlreadyShown)
        if (viewResults.length) sections.push({ label: 'Vistas', items: viewResults.slice(0, 8) })
        if (interfaceResults.length) sections.push({ label: 'Interfaces', items: interfaceResults.slice(0, 5) })

        var customerResults = this.dataResults.filter(item => item.group === 'Clientes').filter(notAlreadyShown)
        var supplierResults = this.dataResults.filter(item => item.group === 'Proveedores').filter(notAlreadyShown)
        var projectResults = this.dataResults.filter(item => item.group === 'Proyectos').filter(notAlreadyShown)
        if (customerResults.length) sections.push({ label: 'Clientes', items: customerResults })
        if (supplierResults.length) sections.push({ label: 'Proveedores', items: supplierResults })
        if (projectResults.length) sections.push({ label: 'Proyectos', items: projectResults })

        return sections
      }

      var defaultSections = []

      // Accesos directos NO se muestra acá: solo aparece con el comando "/"
      // (ver shortcutCommandQuery más arriba) para no competir con
      // Recientes en el estado vacío.
      var recents = this.getRecents()
      if (recents.length) defaultSections.push({ label: 'Recientes', items: recents })

      return defaultSections
    },
    flatItems() {
      var flat = []
      this.sections.forEach(section => {
        section.items.forEach(item => flat.push(item))
      })
      return flat
    },
  },
  methods: {
    setup() {
      var session = Session.getSession()
      this.enabled = Boolean(session.auth &&
                             session.auth.user.profile_id == Profiles.PERSONAL &&
                             !session.auth.user.rol_admin)
    },

    onGlobalKeydown(event) {
      if (!this.enabled) return

      if ((event.ctrlKey || event.metaKey) && (event.key === 'k' || event.key === 'K')) {
        event.preventDefault()
        this.open ? this.close() : this.show()
      }
    },
    show(target) {
      if (!this.enabled) return

      this.pinTarget = target === 'space' ? 'space' : 'shortcuts'
      this.index = Search.buildIndex()
      this.dashboardIndex = Search.buildDashboardIndex()
      this.actionIndex = Search.buildActionIndex()
      this.shortcutIndex = Search.buildShortcutIndex()
      this.recentIndex = Search.indexEntries(this.getRecents())
      this.query = ''
      this.selectedIndex = 0
      this.dataResults = []
      this.dataLoading = false
      clearTimeout(this.dataDebounceTimer)
      this.open = true

      // Las interfaces PHP dependen de un request por módulo accesible, así
      // que se resuelven aparte y se suman al índice ya armado en cuanto
      // llegan — no bloquean la apertura de la paleta.
      Search.loadInterfaces().then(items => {
        if (!this.open || !items.length) return
        this.index = this.index.concat(items)
      })

      this.$nextTick(() => {
        if (this.$refs.input) this.$refs.input.focus()
      })
    },
    close() {
      this.open = false
    },

    moveSelection(delta) {
      var count = this.flatItems.length
      if (!count) return
      var next = this.selectedIndex + delta
      if (next < 0) next = count - 1
      if (next >= count) next = 0
      this.selectedIndex = next
      this.$nextTick(() => this.scrollToActive())
    },
    scrollToActive() {
      var container = this.$refs.results
      if (!container) return
      var active = container.querySelector('.cp-item--active')
      if (active) active.scrollIntoView({ block: 'nearest' })
    },
    confirmSelection() {
      var item = this.flatItems[this.selectedIndex]
      if (item) this.onItemClick(item)
    },
    // En modo "space" (ver pinTarget) el click en la fila suma directo a Mi
    // espacio y cierra la paleta — una sola acción, sin estrella de por
    // medio (esa queda reservada a Favoritos, en la tab ya renderizada, ver
    // DefaultTabsLayout.vue). Fuera de ese modo: click navega, como siempre.
    onItemClick(item) {
      if (this.pinTarget === 'space' && this.canPin(item)) {
        this.addToSpace(item)
        return
      }
      this.activate(item)
    },
    canPin(item) {
      // Las interfaces PHP se guardan como tile de tipo vista (ver
      // shortcuts.js, que normaliza cualquier tipo desconocido a 'view').
      return item.type === 'view' || item.type === 'interface' || item.type === 'action' || item.type === 'dashboard'
    },
    // Misma franja de color que va a tener la tile en Mi Espacio una vez
    // agregada (ver tileTypeVariant en mySpace.vue) — anticipa el tipo
    // desde el resultado de búsqueda, no solo al fijarlo.
    itemTypeClass(item) {
      if (item.type === 'view' || item.type === 'interface') return 'cp-item--type-view'
      if (item.type === 'action') return 'cp-item--type-action'
      if (item.type === 'dashboard') return 'cp-item--type-dashboard'
      return ''
    },
    addToSpace(item) {
      var path = this.itemPath(item)
      if (path) {
        var list = mySpaceShortcuts.add({ title: item.title, fullPath: path, type: item.type })
        EventBus.$emit('SpaceShortcuts:updated', list)
      }
      this.close()
    },
    activate(item) {
      this.close()

      // Si la tab actual dejó un modal de alta abierto (kept-alive, ver
      // DefaultTabsLayout.vue), minimizarlo en vez de dejarlo apilado detrás
      // del que abra la nueva vista/acción.
      EventBus.$emit('modal:minimize-all')

      if (item.to) {
        this.$router.push(item.to).catch(() => {})
        return
      }
      if (item.url && item.url !== this.$route.fullPath) {
        this.$router.push(item.url).catch(() => {})
      }
    },
    flatIndex(item) {
      return this.flatItems.indexOf(item)
    },
    itemKey(item) {
      // item.to.name solo no alcanza: las interfaces PHP comparten el mismo
      // route name (ViewStaffViewPHPCustom) y solo cambia el id por params;
      // varias acciones (facturar/nota de crédito/nota de débito, ajustes de
      // stock, tareas por tipo, etc.) comparten ruta y solo cambian por
      // query. Hay que incluir ambos o se repiten :key y Vue rompe el patch.
      var toKey = item.to && (item.to.name +
        (item.to.params ? ':' + JSON.stringify(item.to.params) : '') +
        (item.to.query ? ':' + JSON.stringify(item.to.query) : ''))
      return item.type + ':' + (item.url || toKey || item.title)
    },

    // BÚSQUEDA DE DATOS (clientes/proveedores/proyectos) — asíncrona,
    // separada internamente del índice local para poder aplicar debounce.
    scheduleDataSearch() {
      clearTimeout(this.dataDebounceTimer)

      // "#..." (tableros) / "@..." (acciones) / "/..." (accesos directos) no
      // buscan registros del sistema.
      if (this.dashboardCommandQuery !== null || this.actionCommandQuery !== null || this.shortcutCommandQuery !== null) {
        this.dataResults = []
        this.dataLoading = false
        return
      }

      var term = this.query.trim()
      if (term.length < DATA_SEARCH_MIN_LENGTH) {
        this.dataResults = []
        this.dataLoading = false
        return
      }

      this.dataLoading = true
      this.dataDebounceTimer = setTimeout(() => this.runDataSearch(term), DATA_SEARCH_DEBOUNCE_MS)
    },
    runDataSearch(term) {
      var requestId = ++this.dataRequestIdCounter

      Search.searchData(term).then(results => {
        // Descarta respuestas viejas si el usuario ya siguió escribiendo.
        if (requestId !== this.dataRequestIdCounter) return
        this.dataResults = results
        this.dataLoading = false
      })
    },

    // Las vistas finas (permissions.items, ver search.js) navegan por `to`
    // (ruta con nombre) en vez de `url` — resolvemos el path acá para que
    // "sumar a Mi espacio" siga guardando un fullPath, sin cambiar el
    // modelo de datos de mySpaceShortcuts.js.
    itemPath(item) {
      if (item.url) return item.url
      if (item.to) return this.$router.resolve(item.to).route.fullPath
      return null
    },

    // RECIENTES
    recentsKey() {
      var auth = Session.getSession().auth
      var userId = auth && auth.user && auth.user.id ? auth.user.id : 'guest'
      var user = auth && auth.user ? auth.user : {}
      var profileId = user.profile_id || 'default'
      // Un mismo usuario puede entrar como personal general o como vendedor.
      // Ambas sesiones navegan a rutas distintas que comparten título (por
      // ejemplo, "Gestión de reporte de cobranzas"), por lo que un historial
      // identificado solo por usuario terminaba mostrando las dos entradas.
      var sellerId = user.seller && user.seller.id ? user.seller.id : 'general'
      return RECENTS_KEY_PREFIX + userId + '_' + profileId + '_' + sellerId
    },
    recentIdentity(item) {
      // El título es el identificador que entiende la persona en Recientes.
      // Si dos rutas del mismo contexto se presentan con el mismo texto,
      // prevalece la última visitada y se evita duplicar el resultado.
      return Search.normalize(item && item.title)
    },
    // Los recientes representan vistas, no registros puntuales. La ruta
    // base evita conservar ids opcionales (ej. /tasks/list/123) o queries
    // transitorios de una navegación puntual.
    recentBasePath(route) {
      if (!route) return ''
      var records = route.matched || []
      var record = records.slice().reverse().find(item => item.name === route.name) || records[records.length - 1]
      var path = record && record.path ? record.path : route.path
      return (path || '').replace(/\/:[^/]+\??/g, '').replace(/\/$/, '') || '/'
    },
    // Un fullPath guardado nunca tiene el id de las vistas de parámetro
    // oculto (nunca estuvo en la URL, ver routeRequiresParam en search.js) —
    // hay que resolver el path a su nombre de ruta para poder preguntar.
    isHiddenParamPath(fullPath) {
      var resolved = this.$router.resolve(fullPath)
      var name = resolved && resolved.route && resolved.route.name
      return name ? Search.routeRequiresParam(name) : false
    },
    // Dashboards de módulo: solo se buscan con "#" (ver
    // dashboardCommandQuery) -- no deben aparecer en Recientes tampoco.
    // trackRecent() ya no los graba de acá en adelante, pero este filtro
    // además limpia entradas guardadas antes de este cambio.
    isDashboardPath(fullPath) {
      var resolved = this.$router.resolve(fullPath)
      var name = resolved && resolved.route && resolved.route.name
      return name ? Search.isDashboardRoute(name) : false
    },
    isDeprecatedPath(fullPath) {
      var resolved = this.$router.resolve(fullPath)
      var name = resolved && resolved.route && resolved.route.name
      return name ? Search.isDeprecatedRoute(name) : false
    },
    getRecents() {
      var raw = Storage.getValue(this.recentsKey())
      if (!raw) return []
      try {
        var list = JSON.parse(raw)
        if (!Array.isArray(list)) return []
        var nav = Search.getNav()
        var currentPath = this.recentBasePath(this.$route)
        var identities = {}
        return list.filter(item => {
          if (!item || !item.fullPath || !item.title) return false
          var identity = this.recentIdentity(item)
          if (!identity || identities[identity]) return false
          identities[identity] = true
          return true
        })
          .filter(item => this.recentBasePath(this.$router.resolve(item.fullPath).route) !== currentPath)
          .filter(item => !this.isHiddenParamPath(item.fullPath))
          .filter(item => !this.isDashboardPath(item.fullPath))
          .filter(item => !this.isDeprecatedPath(item.fullPath))
          .map(item => ({
            type: 'view',
            title: item.title,
            group: 'Reciente',
            url: this.recentBasePath(this.$router.resolve(item.fullPath).route),
            icon: Search.findIconByPath(nav, this.recentBasePath(this.$router.resolve(item.fullPath).route)) || 'fa fa-history',
          }))
      } catch (e) {
        return []
      }
    },
    trackRecent(route) {
      if (!this.enabled) return
      if (!route.meta || !route.meta.title) return
      if (route.path.substring(0, 4) !== '/st/') return
      // No guardar vistas de parámetro oculto — un recién que no lo tiene
      // en la URL queda rota para siempre (ver isHiddenParamPath arriba).
      if (Search.routeRequiresParam(route.name)) return
      // No guardar dashboards de módulo — solo se buscan con "#" (ver
      // isDashboardPath arriba).
      if (Search.isDashboardRoute(route.name)) return
      if (Search.isDeprecatedRoute(route.name)) return

      var raw = Storage.getValue(this.recentsKey())
      var list = []
      try {
        list = raw ? JSON.parse(raw) : []
        if (!Array.isArray(list)) list = []
      } catch (e) {
        list = []
      }

      var recent = { title: route.meta.title, fullPath: this.recentBasePath(route) }
      var recentIdentity = this.recentIdentity(recent)
      list = list.filter(item => {
        return item && item.fullPath !== recent.fullPath && this.recentIdentity(item) !== recentIdentity
      })
      list.unshift(recent)
      list = list.slice(0, RECENTS_MAX)

      Storage.setValue(this.recentsKey(), JSON.stringify(list))
    },
  },
}
</script>

<style scoped>
.cp-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* Por encima de cualquier b-modal (base 1050, +1 por cada uno apilado) para
     que el Ctrl+K siempre quede visible y quedarse con el foco sin pelear
     contra el enforceFocus de un modal abierto debajo (ver main.js). */
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 12vh 16px 16px;
  background: var(--app-color-overlay);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}
.cp-panel {
  width: 100%;
  max-width: 640px;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  background: var(--app-color-surface);
  border-radius: 14px;
  box-shadow: var(--app-shadow-lg);
  overflow: hidden;
}
.cp-mode-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
  font-size: var(--app-text-xs);
  font-weight: var(--app-font-bold);
  border-bottom: 1px solid var(--app-color-border);
}
.cp-input-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--app-color-border);
}
.cp-input-icon {
  flex: 0 0 auto;
  color: var(--app-color-primary);
  font-size: 18px;
}
.cp-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: var(--app-color-text);
  background: transparent;
}
.cp-input::placeholder {
  color: var(--app-color-text-subtle);
}
.cp-esc-hint {
  flex: 0 0 auto;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--app-color-text-subtle);
  border: 1px solid var(--app-color-border);
  border-radius: 5px;
  padding: 2px 6px;
  cursor: pointer;
}
.cp-results {
  flex: 1;
  overflow-y: auto;
  padding: 6px 0 8px;
}
.cp-section-label {
  padding: 10px 16px 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--app-color-text-subtle);
}
.cp-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 16px;
  cursor: pointer;
  box-shadow: inset 3px 0 0 0 var(--cp-item-accent, transparent);
}
/* Misma franja que va a tener la tile de Mi Espacio una vez agregada (ver
   .my-space-tile--action/--dashboard en mySpace.vue) — vista/acción/tablero
   son los únicos 3 tipos que ese sistema de color identifica. --cp-item-tint
   es el fondo al seleccionar (.cp-item--active). Los tres usan el mismo
   nivel de saturación del token ("-200"/"-border", no "-soft"): con
   "-soft" quedaban casi indistinguibles entre sí (ver historial — el verde
   se leía igual que el celeste). Acento y fondo comparten variable a
   propósito, para que no puedan quedar desalineados entre sí de nuevo. */
.cp-item--type-view {
  --cp-item-accent: var(--app-color-primary-200);
  --cp-item-tint: var(--app-color-primary-200);
}
.cp-item--type-action {
  --cp-item-accent: var(--app-color-success-border);
  --cp-item-tint: var(--app-color-success-border);
}
.cp-item--type-dashboard {
  --cp-item-accent: var(--app-color-warning-border);
  --cp-item-tint: var(--app-color-warning-border);
}
.cp-item--active {
  background: linear-gradient(90deg, var(--cp-item-tint, var(--app-color-primary-soft)), transparent);
  box-shadow: inset 3px 0 0 0 var(--cp-item-accent, var(--app-color-primary));
}
.cp-item-icon {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: var(--app-color-surface-muted);
  color: var(--app-color-text-secondary);
  font-size: 14px;
}
.cp-item--active .cp-item-icon {
  background: var(--app-color-primary-soft-hover);
  color: var(--app-color-primary);
}
.cp-item-title {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: var(--app-color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* flex 0 1 auto + max-width: antes solo mostraba códigos cortos (ítems de
   "Datos") y flex:0 0 auto le alcanzaba. Ahora también trae descripciones
   largas (subtitle de m_modules_permissions, ítems de "Vistas") — sin este
   límite, un subtítulo largo se queda con todo el ancho de la fila (no
   encoge) y termina achicando el título hasta casi desaparecer. */
.cp-item-subtitle {
  flex: 0 1 auto;
  max-width: 240px;
  font-size: 11px;
  color: var(--app-color-text-subtle);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cp-item-group {
  flex: 0 0 auto;
  font-size: 11px;
  color: var(--app-color-text-subtle);
}
.cp-item-notes-match {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  gap: 4px;
  padding: 3px 6px;
  border-radius: 5px;
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
}
.cp-item-notes-match .app-icon {
  font-size: 12px;
}
.cp-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 16px;
  color: var(--app-color-text-subtle);
  font-size: 12px;
}
.cp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 36px 16px;
  color: var(--app-color-text-subtle);
  font-size: 13px;
  text-align: center;
}
.cp-footer {
  display: flex;
  gap: 16px;
  padding: 8px 16px;
  border-top: 1px solid var(--app-color-border);
  background: var(--app-color-surface-subtle);
  font-size: 11px;
  color: var(--app-color-text-subtle);
}
.cp-footer kbd {
  display: inline-block;
  min-width: 18px;
  padding: 1px 4px;
  margin-right: 3px;
  border: 1px solid var(--app-color-border);
  border-radius: 4px;
  background: var(--app-color-surface);
  color: var(--app-color-text-muted);
  font-size: 10px;
  text-align: center;
}
/* Comandos (#/@//) empujados al extremo derecho y con un divisor propio --
   son la ayuda que más vale la pena destacar (el resto es navegación básica
   ya conocida), así que van con tinte de color en vez del gris neutro. */
.cp-footer-commands {
  display: flex;
  gap: 14px;
  margin-left: auto;
  padding-left: 14px;
  border-left: 1px solid var(--app-color-border);
}
.cp-footer-command {
  color: var(--app-color-primary);
  font-weight: var(--app-font-semibold);
}
.cp-footer-command kbd {
  border-color: var(--app-color-primary-200);
  background: var(--app-color-primary-soft);
  color: var(--app-color-primary);
}
.cp-fade-enter-active,
.cp-fade-leave-active {
  transition: opacity 0.12s ease;
}
.cp-fade-enter-active .cp-panel,
.cp-fade-leave-active .cp-panel {
  transition: transform 0.12s ease;
}
.cp-fade-enter,
.cp-fade-leave-to {
  opacity: 0;
}
.cp-fade-enter .cp-panel {
  transform: scale(0.98) translateY(-6px);
}
@media (max-width: 575.98px) {
  .cp-backdrop {
    padding: 8px;
    align-items: stretch;
  }
  .cp-panel {
    max-height: none;
    border-radius: 12px;
  }
  .cp-footer {
    display: none;
  }
  /* El subtítulo sigue formando parte del índice de búsqueda, pero en la
     pantalla chica se prioriza que el nombre de la vista sea legible. */
  .cp-item-subtitle {
    display: none;
  }
}
</style>
